import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Download, Link2 } from "lucide-react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { RemixDialog } from "./RemixDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RemixesPanel = ({ token, onCountChange }) => {
  const [remixes, setRemixes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchRemixes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/remixes`);
      setRemixes(res.data);
      onCountChange?.(res.data.length);
    } catch (err) {
      console.error("Failed to fetch remixes:", err);
    }
  }, [onCountChange]);

  useEffect(() => { fetchRemixes(); }, [fetchRemixes]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/remixes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Remix deleted");
      fetchRemixes();
    } catch (err) {
      toast.error("Failed to delete remix");
    }
  };

  const copyLink = async (remix) => {
    const url = `${window.location.origin}/remix/${remix.slug || remix.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied", { description: url });
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  return (
    <div data-testid="remixes-panel">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl tracking-tighter">Remixes & Bootlegs</h2>
        <button
          data-testid="add-remix-btn"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="btn-alchemist flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white hover:text-black py-3 px-6 uppercase tracking-widest text-xs font-sub transition-colors duration-300"
        >
          <Plus className="w-3 h-3" />
          Add Remix
        </button>
      </div>
      {remixes.length === 0 ? (
        <div className="text-center py-24 text-white/20">
          <Download className="w-8 h-8 mx-auto mb-4 opacity-30" />
          <p className="font-sub text-xs tracking-widest">No remixes yet</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="font-sub text-[10px] tracking-widest text-white/40">ORDER</TableHead>
              <TableHead className="font-sub text-[10px] tracking-widest text-white/40">TRACK</TableHead>
              <TableHead className="font-sub text-[10px] tracking-widest text-white/40">TYPE</TableHead>
              <TableHead className="font-sub text-[10px] tracking-widest text-white/40">FILE</TableHead>
              <TableHead className="font-sub text-[10px] tracking-widest text-white/40 text-right">DOWNLOADS</TableHead>
              <TableHead className="font-sub text-[10px] tracking-widest text-white/40 text-right">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {remixes.map((remix) => (
              <TableRow key={remix.id} data-testid={`remix-row-${remix.id}`} className="border-white/5 hover:bg-white/[0.02]">
                <TableCell className="font-sub text-xs text-white/40">{remix.sort_order}</TableCell>
                <TableCell>
                  <p className="text-white font-medium">{remix.title}</p>
                  {remix.original_artist && <p className="font-sub text-[10px] text-white/30 tracking-wide">{remix.original_artist}</p>}
                  {remix.slug && <p className="font-sub text-[10px] text-white/20 tracking-wide" data-testid={`remix-slug-${remix.id}`}>/remix/{remix.slug}</p>}
                </TableCell>
                <TableCell className="font-sub text-xs text-white/50">{remix.remix_type}</TableCell>
                <TableCell className="font-sub text-xs text-white/40 truncate max-w-xs">
                  {remix.audio_filename || remix.audio_url || "—"}
                  {remix.audio_size ? ` · ${(remix.audio_size / (1024 * 1024)).toFixed(0)} MB` : ""}
                </TableCell>
                <TableCell className="font-sub text-xs text-white/60 text-right" data-testid={`remix-downloads-${remix.id}`}>{remix.download_count || 0}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger className="font-sub text-xs text-white/30 hover:text-white tracking-widest transition-colors duration-300 px-2 py-1">...</DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#0f0f0f] border-white/10">
                      <DropdownMenuItem data-testid={`copy-link-remix-${remix.id}`} onClick={() => copyLink(remix)} className="text-white/70 hover:text-white cursor-pointer">
                        <Link2 className="w-3 h-3 mr-2" /> Copy share link
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`edit-remix-${remix.id}`} onClick={() => { setEditing(remix); setDialogOpen(true); }} className="text-white/70 hover:text-white cursor-pointer">
                        <Edit2 className="w-3 h-3 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem data-testid={`delete-remix-${remix.id}`} onClick={() => handleDelete(remix.id)} className="text-red-400 hover:text-red-300 cursor-pointer">
                        <Trash2 className="w-3 h-3 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <RemixDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} token={token} count={remixes.length} onSaved={fetchRemixes} />
    </div>
  );
};
